<template>
    <div id="test-app-dt">
        <div class="container">
            <DataTable
                :columns="serieColumns"
                :items="items"
                :current-page="currentPage"
                :total-pages="totalPages"
                :enable-multi-select="false"
                :enable-drag-drop="false"
                @page-changed="handlePageChange"
                @sort="onSort"
            >
                <template #toolbar>
                    <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                        <div class="ppv2-flex-1"></div>

                        <!-- Search Input -->
                        <div class="ppv2-relative">
                            <Search v-model="searchQuery" :emitGlobal="false" @search-updated="onSearch" />
                        </div>
                    </div>
                </template>

                <template #column-expiration="{ item }">
                    <span v-if="[0, 1].includes(item.expiration)" :class="statusExpired[item.expiration].class">
                        {{ statusExpired[item.expiration].label }} {{ item.expire_at }}
                    </span>
                    <span v-else :class="statusExpired[item.expiration].class">{{ statusExpired[item.expiration].label }}</span>
                </template>

                <template #column-prestations="{ item }">
                    {{ item.prestations.join(',') }}
                </template>

                <template #column-linked_appointments="{ item }">
                    <div v-for="(la, index) in getLinkedApp(item.linked_appointments)" :key="index">
                        <a :href="la['url']">{{ la['label'] }}</a>
                    </div>
                </template>

                <template #column-payment="{ item }">
                    {{ getPaymentLabel(item.payment) }}
                </template>

                <template #column-actions="{ item }">
                    <div
                        class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3"
                        :style="{ fontSize: normalizeFontSize(18) }"
                    >
                        <a
                            :href="`/admin/planning?modaladd=true&fillclient=${user.id}`"
                            class="ppv2-p-1 ppv2-text-black hover:ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            :title="$t('datatable_customer_serie_v2_edit.actions.btn_chedule.title')"
                        >
                            <Calendar :size="18" />
                        </a>
                    </div>
                </template>
            </DataTable>
        </div>
    </div>
</template>

<script>
import DataTable from '../../Shared/DataTable.vue'
import { Calendar } from 'lucide-vue'
import normalizeSize from '../../../utils/normalizeSize'
import Search from '../../V3/Experience/components/Search.vue'
import normalizeFontSize from '../../../utils/normalizeFontSize'

export default {
    name: 'App',
    props: ['user'],
    components: {
        DataTable,
        Calendar,
        Search,
    },
    data() {
        return {
            // Data Table State
            items: [],
            loading: false,
            currentPage: 1,
            perPage: 10,
            totalEntries: 0,
            searchQuery: '',
            sortKey: '',
            sortOrder: 'asc',
            searchTimeout: null,

            // API Configuration
            statusExpired: [
                {
                    label: this.$t('datatable_customer_serie_v2_edit.status_item.expires'),
                    class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-green-600 ppv2-text-green-100 ppv2-text-xs ppv2-px-3 ppv2-py-1',
                },
                {
                    label: this.$t('datatable_customer_serie_v2_edit.status_item.expired_on'),
                    class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-pink-300 ppv2-text-pink-100 ppv2-text-xs ppv2-px-3 ppv2-py-1',
                },
                {
                    label: this.$t('datatable_customer_serie_v2_edit.status_item.none'),
                    class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-rose-300 ppv2-text-rose-100 ppv2-text-xs ppv2-px-3 ppv2-py-1',
                },
            ],
            dureePayment: {
                7: this.$t('datatable_customer_serie_v2_edit.duree_inline.every_week'),
                15: this.$t('datatable_customer_serie_v2_edit.duree_inline.every_fifteen_days'),
                30: this.$t('datatable_customer_serie_v2_edit.duree_inline.every_month'),
                60: this.$t('datatable_customer_serie_v2_edit.duree_inline.every_two_months'),
                90: this.$t('datatable_customer_serie_v2_edit.duree_inline.every_three_months'),
                365: this.$t('datatable_customer_serie_v2_edit.duree_inline.once_a_year'),
            },

            apiHeaders: {
                Authorization: 'Bearer your-token-here',
                'X-Requested-With': 'XMLHttpRequest',
            },

            // Users Table Configuration
            seriePurhaseApiUrl: '/back-office/v2/customer/' + this.user.id + '/ajaxSeriePurchase',
            serieColumns: [
                {
                    key: 'name',
                    label: this.$t('datatable_customer_serie_v2_edit.name'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'created_at',
                    label: this.$t('datatable_customer_serie_v2_edit.date_of_purchase'),
                    sortable: true,
                    type: 'date',
                },
                {
                    key: 'price',
                    label: this.$t('datatable_customer_serie_v2_edit.price_of_purchase'),
                    sortable: true,
                    type: 'number',
                },
                {
                    key: 'prestations',
                    label: this.$t('datatable_customer_serie_v2_edit.authorized_activities'),
                    sortable: false,
                    type: 'text',
                },
                {
                    key: 'initial_quantity',
                    label: this.$t('datatable_customer_serie_v2_edit.initial_quantity'),
                    sortable: true,
                    type: 'number',
                },
                {
                    key: 'rest_quantity',
                    label: this.$t('datatable_customer_serie_v2_edit.remaining_quantity'),
                    sortable: true,
                    type: 'number',
                },
                {
                    key: 'expiration',
                    label: this.$t('datatable_customer_serie_v2_edit.expiry'),
                    sortable: true,
                    type: 'date',
                },
                {
                    key: 'payment',
                    label: this.$t('datatable_customer_serie_v2_edit.payment'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'next_payment',
                    label: this.$t('datatable_customer_serie_v2_edit.next_collection'),
                    sortable: true,
                    type: 'date',
                },
                {
                    key: 'linked_appointments',
                    label: this.$t('datatable_customer_serie_v2_edit.linked_reservation'),
                    sortable: false,
                    type: 'text',
                },
                {
                    key: 'actions',
                    label: this.$t('datatable_customer_serie_v2_edit.action'),
                    sortable: false,
                    type: 'actions',
                },
            ],
        }
    },
    computed: {
        totalPages() {
            return Math.ceil(this.totalEntries / this.perPage)
        },
    },
    mounted() {
        this.fetchData()
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        async fetchData() {
            this.loading = true
            try {
                const params = {
                    page: this.currentPage,
                    per_page: this.perPage,
                    search: this.searchQuery,
                    sort_by: this.sortKey,
                    sort_order: this.sortOrder,
                }
                const response = await this.makeApiRequest(params)
                this.handleApiResponse(response)
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.onError(error)
            } finally {
                this.loading = false
            }
        },
        async makeApiRequest(params) {
            const queryString = new URLSearchParams(params).toString()
            const url = `${this.seriePurhaseApiUrl}?${queryString}`

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.apiHeaders,
                },
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            return await response.json()
        },
        handleApiResponse(response) {
            let _response = response.data
            if (_response.data && Array.isArray(_response.data)) {
                this.items = _response.data
                this.totalEntries = _response.total || _response.data.length
                this.onDataLoaded({ data: this.items, total: this.totalEntries })
            } else if (Array.isArray(_response)) {
                this.items = _response
                this.totalEntries = _response.length
                this.onDataLoaded({ data: this.items, total: this.totalEntries })
            } else {
                this.items = []
                this.totalEntries = 0
            }
        },
        handlePageChange(page) {
            this.currentPage = page
            this.fetchData()
        },
        onSearch() {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.currentPage = 1
                this.fetchData()
            }, 500)
        },
        onSort(key) {
            if (this.sortKey === key) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortKey = key
                this.sortOrder = 'asc'
            }
            this.currentPage = 1
            this.fetchData()
        },
        onDataLoaded(response) {
            console.log('Data loaded:', response)
        },
        onError(error) {
            console.error('Datatable error:', error)
            alert('Error loading data: ' + error.message)
        },
        getLinkedApp(linked_appointments) {
            return linked_appointments.map((slot) => {
                const date = new Date(slot.start * 1000) // Convert UNIX timestamp to JS Date
                const label = date.toLocaleString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                })
                const url = `${window.location.origin}/back-office/planning?date=${encodeURIComponent(
                    slot.start
                )}&timeslot=${encodeURIComponent(slot.id)}`
                return {
                    label,
                    url,
                }
            })
        },
        getPaymentLabel(duree) {
            return this.dureePayment[duree] || this.$t('datatable_customer_serie_v2_edit.duree_inline.only')
        },
    },
}
</script>

<style scoped>
.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
}
</style>
